import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/authStore'
import { TeamType } from '../../types'

const teams: TeamType[] = [
  '1. Mannschaft', '2. Mannschaft', '3. Mannschaft',
  'Damen',
  '50+', '40+', '30+',
  'BA', 'BB', 'CA', 'CB', 'FF-14', 'FF-17',
  'D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C',
  'EA', 'EB', 'EC', 'ED', 'FA', 'FB', 'G'
]

interface TestspielSlot {
  id: string
  date: string
  time: string
  status: 'available' | 'pending' | 'confirmed' | 'cancelled'
  team: string | null
  opponent: string | null
}

interface DateTimeOption {
  value: string // date|time format
  weekday: string
  date: string
  time: string
  status: 'available' | 'reserved' | 'occupied'
  statusText: string
}

export default function AddTestspielForm() {
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  
  const [selectedTeam, setSelectedTeam] = useState<TeamType>('1. Mannschaft')
  const [selectedDateTime, setSelectedDateTime] = useState('')
  const [opponent, setOpponent] = useState('')
  const [trainerName, setTrainerName] = useState('')
  const [notes, setNotes] = useState('')
  
  const [allSlots, setAllSlots] = useState<TestspielSlot[]>([])
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get valid times for each team type
  const getValidTimesForTeam = (team: TeamType, date: string): string[] => {
    const dayOfWeek = new Date(date + 'T00:00:00').getDay() // 0 = Sunday, 6 = Saturday
    
    // Active teams (1., 2., 3. Mannschaft, Damen): Saturday 15:00, 17:30 and Sunday 15:00
    if (['1. Mannschaft', '2. Mannschaft', '3. Mannschaft', 'Damen'].includes(team)) {
      if (dayOfWeek === 6) return ['15:00', '17:30'] // Saturday
      if (dayOfWeek === 0) return ['15:00'] // Sunday
      return []
    }
    
    // Seniors (50+, 40+, 30+): Saturday 20:00
    if (['50+', '40+', '30+'].includes(team)) {
      if (dayOfWeek === 6) return ['20:00'] // Saturday
      return []
    }
    
    // B- and C-Juniors (BA, BB, CA, CB): Saturday 12:30
    if (['BA', 'BB', 'CA', 'CB'].includes(team)) {
      if (dayOfWeek === 6) return ['12:30'] // Saturday
      return []
    }
    
    // D-Juniors (D9A, D9B, D9C, D7A, D7B, D7C, FF-17): Saturday 10:00 and Sunday 12:30
    if (['D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C', 'FF-17'].includes(team)) {
      if (dayOfWeek === 6) return ['10:00'] // Saturday
      if (dayOfWeek === 0) return ['12:30'] // Sunday
      return []
    }
    
    // E-Juniors, FF-14, F-Juniors, G: Sunday 10:00
    if (['EA', 'EB', 'EC', 'ED', 'FF-14', 'FA', 'FB', 'G'].includes(team)) {
      if (dayOfWeek === 0) return ['10:00'] // Sunday
      return []
    }
    
    return []
  }

  // Fetch slots when component mounts
  useEffect(() => {
    fetchAllSlots()
  }, [])

  // Prefill trainer name
  useEffect(() => {
    if (profile?.name && !trainerName) {
      setTrainerName(profile.name)
    }
  }, [profile?.name, trainerName])

  const fetchAllSlots = async () => {
    console.log('🚀 Starting fetchAllSlots...')
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('testspiele')
        .select('id, date, time, status, team, opponent')
        .order('date', { ascending: true })
        .order('time', { ascending: true })

      if (error) {
        console.error('❌ Error fetching slots:', error)
        throw error
      }
      
      console.log('✅ Fetched slots:', data?.length || 0)
      console.log('📊 Sample slots:', data?.slice(0, 5))
      
      setAllSlots(data || [])
    } catch (error) {
      console.error('❌ Error fetching testspiele slots:', error)
    } finally {
      setLoading(false)
    }
  }

  // Create date/time options for the selected team
  const dateTimeOptions = useMemo(() => {
    console.log('🔍 Processing dateTimeOptions for team:', selectedTeam)
    console.log('📊 Total allSlots:', allSlots.length)
    
    if (!selectedTeam) return []
    
    const options: DateTimeOption[] = []
    const processedSlots = new Set<string>()
    
    allSlots.forEach((slot, index) => {
      console.log(`  Slot ${index + 1}:`, slot)
      
      const validTimes = getValidTimesForTeam(selectedTeam, slot.date)
      console.log(`    Valid times for ${selectedTeam} on ${slot.date}:`, validTimes)
      
      // Normalize time format - remove seconds if present
      const normalizedSlotTime = slot.time.substring(0, 5) // "10:00:00" -> "10:00"
      console.log(`    Slot time normalized: "${slot.time}" -> "${normalizedSlotTime}"`)
      
      // Only include if this time is valid for the selected team
      if (!validTimes.includes(normalizedSlotTime)) {
        console.log(`    ❌ Time ${normalizedSlotTime} not valid for team`)
        return
      }

      const slotKey = `${slot.date}|${normalizedSlotTime}`
      if (processedSlots.has(slotKey)) {
        console.log(`    ⚠️ Duplicate slot key: ${slotKey}`)
        return
      }
      processedSlots.add(slotKey)

      // Format the date
      const dateObj = new Date(slot.date + 'T00:00:00')
      const weekday = dateObj.toLocaleDateString('de-DE', { weekday: 'long' })
      const dateStr = dateObj.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit',
        year: 'numeric'
      })

      // Determine status
      let status: 'available' | 'reserved' | 'occupied' = 'available'
      let statusText = 'verfügbar'

      const hasTeam = slot.team !== null && slot.team !== undefined && slot.team.trim() !== ''

      if (!hasTeam && slot.status === 'available') {
        status = 'available'
        statusText = 'verfügbar'
      } else if (hasTeam && slot.status === 'pending') {
        status = 'reserved'
        statusText = 'reserviert'
      } else if (hasTeam && slot.status === 'confirmed') {
        status = 'occupied'
        statusText = 'besetzt'
      } else {
        status = 'available'
        statusText = 'verfügbar'
      }

      console.log(`    ✅ Adding option: ${slotKey} (${statusText})`)

      options.push({
        value: slotKey,
        weekday,
        date: dateStr,
        time: normalizedSlotTime,
        status,
        statusText
      })
    })
    
    console.log('📈 Final options count:', options.length)
    console.log('📋 Options:', options)
    
    return options.sort((a, b) => {
      const dateA = new Date(a.value.split('|')[0] + 'T' + a.time)
      const dateB = new Date(b.value.split('|')[0] + 'T' + b.time)
      return dateA.getTime() - dateB.getTime()
    })
  }, [selectedTeam, allSlots])

  const isFormValid = selectedTeam && selectedDateTime && opponent.trim() && trainerName.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedDateTime || !opponent.trim() || !trainerName.trim()) {
      alert('Bitte alle Pflichtfelder ausfüllen')
      return
    }

    setIsSubmitting(true)
    
    try {
      const [date, time] = selectedDateTime.split('|')
      
      // Find the slot to update - need to handle both time formats
      const existingSlot = allSlots.find(s => {
        const normalizedSlotTime = s.time.substring(0, 5) // normalize database time
        return s.date === date && normalizedSlotTime === time
      })
      
      console.log('🔍 Looking for slot:', { date, time })
      console.log('📊 Available slots:', allSlots.map(s => ({ 
        date: s.date, 
        time: s.time, 
        normalized: s.time.substring(0, 5) 
      })))
      console.log('✅ Found slot:', existingSlot)
      
      if (!existingSlot) {
        throw new Error('Slot nicht gefunden')
      }

      // Update the slot with testspiel data
      const { error } = await supabase
        .from('testspiele')
        .update({
          team: selectedTeam,
          opponent: opponent.trim(),
          trainer_name: trainerName.trim(),
          notes: notes.trim() || null,
          status: 'pending',
          location: 'Vereinsplatz FC Frick',
          user_id: profile?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSlot.id)

      if (error) throw error

      // Send email notification
      try {
        console.log('📧 Sending testspiel email notification...')
        const { sendEmail, createTestspielEmail } = await import('../utils/emailService')
        
        const testspielData = {
          team: selectedTeam,
          opponent: opponent.trim(),
          date: date,
          time: time,
          trainerName: trainerName.trim(),
          notes: notes.trim(),
          location: 'Vereinsplatz FC Frick'
        }
        
        const emailData = createTestspielEmail(testspielData, profile?.email || '')
        const emailResult = await sendEmail(emailData)
        
        if (emailResult.success) {
          console.log('✅ Email sent successfully')
        } else {
          console.error('❌ Email failed:', emailResult.error)
          // Don't block the form submission if email fails
        }
      } catch (emailError) {
        console.error('❌ Email error:', emailError)
        // Don't block the form submission if email fails
      }

      alert('Testspiel erfolgreich erstellt!')
      navigate('/testspiele')
      
    } catch (error) {
      console.error('Error creating testspiel:', error)
      alert('Fehler beim Erstellen des Testspiels')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '24px',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <button
          onClick={() => navigate('/testspiele')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
            e.currentTarget.style.transform = 'translateY(-1px)'
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
        >
          <ArrowLeft size={16} />
          Zurück
        </button>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '800',
          background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          margin: '0',
          letterSpacing: '-0.025em'
        }}>Neues Testspiel</h1>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '20px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e5e7eb',
        padding: '32px'
      }}>
        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* Team Selection */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Team *
            </label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value as TeamType)}
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#22C55E'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              {teams.map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          {/* Date/Time Selection */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Datum und Uhrzeit *
            </label>
            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '12px',
              padding: '12px',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '8px',
              border: '1px solid #bae6fd',
              fontWeight: '500'
            }}>
              <strong>Legende:</strong> ✅ Verfügbar • ⏳ Reserviert • ❌ Besetzt
            </div>
            {loading ? (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                color: '#6B7280',
                background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #f3f4f6',
                  borderTop: '3px solid #22C55E',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 12px'
                }}></div>
                Lade Termine...
              </div>
            ) : (
              <select
                value={selectedDateTime}
                onChange={(e) => setSelectedDateTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '12px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#22C55E'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <option value="">-- Datum und Uhrzeit wählen --</option>
                {dateTimeOptions.map((option) => (
                  <option 
                    key={option.value} 
                    value={option.value}
                    disabled={option.status !== 'available'}
                    style={{
                      color: option.status === 'available' ? '#16a34a' : 
                             option.status === 'reserved' ? '#ea580c' : '#dc2626',
                      fontWeight: option.status === 'available' ? 'normal' : 'bold'
                    }}
                  >
                    {option.status === 'available' ? '✅' : 
                     option.status === 'reserved' ? '⏳' : '❌'} {option.weekday}, {option.date} um {option.time} Uhr ({option.statusText})
                  </option>
                ))}
              </select>
            )}
            {dateTimeOptions.length === 0 && !loading && (
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
                marginTop: '8px',
                padding: '12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '8px',
                border: '1px solid #f3e8ff',
                fontWeight: '500'
              }}>
                Keine verfügbaren Termine für <strong>{selectedTeam}</strong> gefunden.
              </div>
            )}
          </div>

          {/* Opponent */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Gegner *
            </label>
            <input
              type="text"
              value={opponent}
              onChange={(e) => setOpponent(e.target.value)}
              placeholder="Name des Gegners"
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.2s ease',
                outline: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#22C55E'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              required
            />
          </div>

          {/* Trainer */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Trainer/Verantwortlicher *
            </label>
            <input
              type="text"
              value={trainerName}
              onChange={(e) => setTrainerName(e.target.value)}
              placeholder="Name des Trainers"
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.2s ease',
                outline: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#22C55E'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Notizen (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Zusätzliche Informationen..."
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.2s ease',
                outline: 'none',
                resize: 'vertical',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                minHeight: '100px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#22C55E'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            />
          </div>

          {/* Submit */}
          <div style={{
            display: 'flex',
            gap: '16px',
            paddingTop: '8px'
          }}>
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px 24px',
                background: (isSubmitting || !isFormValid) 
                  ? '#9CA3AF' 
                  : 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: (isSubmitting || !isFormValid) ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: (isSubmitting || !isFormValid) 
                  ? '0 2px 4px rgba(156, 163, 175, 0.3)' 
                  : '0 4px 6px -1px rgba(27, 126, 58, 0.3)',
                transform: 'translateY(0)',
                opacity: (isSubmitting || !isFormValid) ? '0.6' : '1'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && isFormValid) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #15803D 0%, #16A34A 100%)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(27, 126, 58, 0.4)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && isFormValid) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                }
              }}
            >
              {isSubmitting ? (
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <Save size={20} />
              )}
              {isSubmitting ? 'Erstelle...' : 'Testspiel erstellen'}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/testspiele')}
              style={{
                flex: '1',
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
